import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useRecoilValue } from 'recoil';

import { menuTestIDs, settingsMenuTestIDs, settingsPageTestIDs } from '@constants';
import { useAppContextNodeHandle, useContextReferences } from '@hooks';
import { screenReaderEnabledState } from '@store';

import { styles } from './styles';

import { canPurchaseOnPlatform, DEBUG } from '../../../helpers/Platform';
import { SettingsMenuItem } from '../SettingsMenuItem/SettingsMenuItem';

const tPath = 'settingsPage.menu';

type SettingsMenuItemNames = keyof typeof settingsMenuTestIDs;

type SettingsMenuProps = {
  activeMenuItem: SettingsMenuItemNames;
  setActiveMenuItem: (val: SettingsMenuItemNames) => void;
};

export const SettingsMenu: FC<SettingsMenuProps> = ({ activeMenuItem, setActiveMenuItem }) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });

  const menuHandle = useAppContextNodeHandle(menuTestIDs.mainMenu);

  const screenreaderEnabled = useRecoilValue(screenReaderEnabledState);

  const settingsMenuItems = [
    {
      accessibilityLabel: t('account.accessibilityLabel'),
      accessibilityHint: t('account.accessibilityHint'),
      title: t('account.label'),
      testID: settingsMenuTestIDs.account,
      labelTestID: settingsMenuTestIDs.accountLabel,
      nextFocus: {
        right: canPurchaseOnPlatform
          ? settingsPageTestIDs.account.settingsAccountChangePlanButton
          : screenreaderEnabled
          ? settingsPageTestIDs.account.settingsAccountInfo
          : null,
        up: menuHandle,
      },
    },
    {
      accessibilityLabel: t('locations.accessibilityLabel'),
      accessibilityHint: t('locations.accessibilityHint'),
      title: t('locations.label'),
      testID: settingsMenuTestIDs.locations,
      labelTestID: settingsMenuTestIDs.locationsLabel,
      nextFocus: {
        right: settingsPageTestIDs.locations.settingsChangeLocationButton,
      },
    },
    {
      accessibilityLabel: t('alerts.accessibilityLabel'),
      accessibilityHint: t('alerts.accessibilityHint'),
      title: t('alerts.label'),
      testID: settingsMenuTestIDs.alerts,
      labelTestID: settingsMenuTestIDs.alertsLabel,
      nextFocus: { right: settingsPageTestIDs.alerts.settingsAlertsSwitch },
    },
    {
      accessibilityLabel: t('closedCaptioning.accessibilityLabel'),
      accessibilityHint: t('closedCaptioning.accessibilityHint'),
      title: t('closedCaptioning.label'),
      testID: settingsMenuTestIDs.closedCaptioning,
      labelTestID: settingsMenuTestIDs.closedCaptioningLabel,
      nextFocus: {
        right: settingsPageTestIDs.closedCaptioning.settingsClosedCaptioningSwitch,
      },
    },
    {
      accessibilityLabel: t('help.accessibilityLabel'),
      accessibilityHint: t('help.accessibilityHint'),
      title: t('help.label'),
      testID: settingsMenuTestIDs.help,
      labelTestID: settingsMenuTestIDs.helpLabel,
      nextFocus: {
        right: screenreaderEnabled ? settingsPageTestIDs.help.settingsHelp : null,
      },
    },
    {
      accessibilityLabel: t('privacyLegal.accessibilityLabel'),
      accessibilityHint: t('privacyLegal.accessibilityHint'),
      title: t('privacyLegal.label'),
      testID: settingsMenuTestIDs.privacyLegal,
      labelTestID: settingsMenuTestIDs.privacyLegalLabel,
      nextFocus: {
        right: screenreaderEnabled ? settingsPageTestIDs.privacyLegal.settingsPrivacyLegal : null,
      },
    },
    {
      accessibilityLabel: t('signOut.accessibilityLabel'),
      accessibilityHint: t('signOut.accessibilityHint'),
      title: t('signOut.label'),
      testID: settingsMenuTestIDs.signOut,
      labelTestID: settingsMenuTestIDs.signOutLabel,
      nextFocus: { right: settingsPageTestIDs.signOut.settingsSignOutButton },
    },
  ];

  if (DEBUG) {
    settingsMenuItems.splice(-1, 0, {
      accessibilityLabel: t('debug.accessibilityLabel'),
      accessibilityHint: t('debug.accessibilityHint'),
      title: t('debug.label'),
      testID: settingsMenuTestIDs.debug,
      labelTestID: settingsMenuTestIDs.debugLabel,
      nextFocus: { right: settingsPageTestIDs.debug.settingsDebugMenuButton },
    });
  }

  for (const [index, item] of settingsMenuItems.entries()) {
    // Set accessibility hints for this menu item in context of index and total settings menu length
    item.accessibilityLabel = `${item.accessibilityLabel}${t('shared.accessibilityLabel', {
      index: index + 1,
      length: settingsMenuItems.length,
    })}`;
    // The last item does not have any more settings below it
    if (index + 1 < settingsMenuItems.length) {
      item.accessibilityHint = `${item.accessibilityHint}${t('shared.accessibilityHint')}`;
    }
  }

  const settingsMenuReferences = useContextReferences(settingsMenuItems.map(menuItem => menuItem.testID));

  return (
    <View style={styles.settingsMenuContent}>
      {settingsMenuItems.map((item, index) => (
        <SettingsMenuItem
          key={item.testID}
          index={index}
          isActive={activeMenuItem === item.testID}
          item={item}
          nextFocus={item.nextFocus}
          setActiveMenuItem={setActiveMenuItem}
          settingsMenuItemsLength={settingsMenuItems.length}
          settingsMenuReferences={settingsMenuReferences}
        />
      ))}
    </View>
  );
};
